import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Search, Printer, Trash2, Plus, Minus, Loader2, ChevronDown, ChevronRight, Barcode, QrCode } from 'lucide-react';
import type { ItemVariant, POSItem } from '@/types';
import { posApi } from '@/services/api';
import { printBarcodeLabels } from '@/utils/barcodeLabelPrint';
import { formatMoney } from '@/utils/currency';

interface BulkPrintLabelsModalProps {
  onClose: () => void;
}

interface QueuedItem {
  key: string; // unique identifier (e.g. item.name + '_' + variant.name)
  item: POSItem;
  variant?: ItemVariant;
  copies: number;
}

export default function BulkPrintLabelsModal({ onClose }: BulkPrintLabelsModalProps) {
  const [modalSearch, setModalSearch] = useState('');
  const [modalCategory, setModalCategory] = useState('All');
  const [printQueue, setPrintQueue] = useState<QueuedItem[]>([]);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [loadedVariants, setLoadedVariants] = useState<Record<string, ItemVariant[]>>({});
  const [loadingVariants, setLoadingVariants] = useState<Record<string, boolean>>({});

  // Override options
  const [codeType, setCodeType] = useState<'Barcode' | 'QR Code' | null>(null);
  const [printPrice, setPrintPrice] = useState<boolean | null>(null);
  const [printCategory, setPrintCategory] = useState(false);

  // Fetch POS settings for defaults
  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message;
    },
  });

  const resolvedCodeType = codeType ?? posSettings?.use_barcode_or_qr ?? 'Barcode';
  const resolvedPrintPrice = printPrice ?? posSettings?.print_price_on_label ?? true;

  // Fetch categories
  const { data: categories = [] } = useQuery<{ name: string; category_name: string; category_type: string }[]>({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return res.data.message;
    },
  });

  const productCategories = useMemo(() => {
    return categories.filter((c) => c.category_type === 'Product');
  }, [categories]);

  // Fetch items list matching filters
  const { data: searchResults = [], isLoading: isLoadingItems } = useQuery({
    queryKey: ['bulkPrintItems', modalSearch, modalCategory],
    queryFn: async () => {
      const res = await posApi.getItems({
        search: modalSearch,
        category: modalCategory === 'All' ? undefined : modalCategory,
        item_type: 'Product',
        limit: 100,
      });
      return (res.data.message.items as POSItem[]) || [];
    },
  });

  // Fetch variants for an item on demand
  const handleToggleExpand = async (item: POSItem) => {
    const isExpanded = !!expandedItems[item.name];
    setExpandedItems((prev) => ({ ...prev, [item.name]: !isExpanded }));

    if (!isExpanded && !loadedVariants[item.name] && !loadingVariants[item.name]) {
      setLoadingVariants((prev) => ({ ...prev, [item.name]: true }));
      try {
        const res = await posApi.getItemVariants(item.name);
        const variants = (res.data.message as ItemVariant[]) || [];
        setLoadedVariants((prev) => ({ ...prev, [item.name]: variants }));
      } catch (err) {
        console.error('Failed to load item variants', err);
      } finally {
        setLoadingVariants((prev) => ({ ...prev, [item.name]: false }));
      }
    }
  };

  // Add item / variant to queue
  const handleAddToQueue = (item: POSItem, variant?: ItemVariant) => {
    const key = variant ? `${item.name}_${variant.name}` : item.name;
    setPrintQueue((prev) => {
      const existing = prev.find((qi) => qi.key === key);
      if (existing) {
        return prev.map((qi) => (qi.key === key ? { ...qi, copies: qi.copies + 1 } : qi));
      }
      return [
        ...prev,
        {
          key,
          item,
          variant,
          copies: 1,
        },
      ];
    });
  };

  // Remove from queue
  const handleRemoveFromQueue = (key: string) => {
    setPrintQueue((prev) => prev.filter((qi) => qi.key !== key));
  };

  // Update copies
  const handleUpdateCopies = (key: string, value: number) => {
    const copies = Math.max(1, Math.min(500, value));
    setPrintQueue((prev) => prev.map((qi) => (qi.key === key ? { ...qi, copies } : qi)));
  };

  const handleClearQueue = () => {
    setPrintQueue([]);
  };

  const totalTags = useMemo(() => {
    return printQueue.reduce((acc, qi) => acc + qi.copies, 0);
  }, [printQueue]);

  const handlePrintBatch = async () => {
    if (!printQueue.length) {
      alert('Your print queue is empty. Add products to print labels.');
      return;
    }

    // SD-UK-048: fall back to item_code / name so labels print without a stored barcode
    const labelsToPrint = printQueue.flatMap((qi) => {
      const title = qi.variant?.variant_name || qi.item.item_name;
      const subtitle = qi.variant ? qi.item.item_name : undefined;
      const barcode =
        qi.variant?.barcode ||
        qi.item.barcode ||
        qi.item.item_code ||
        qi.item.name ||
        '';
      const price = qi.variant?.price ?? qi.item.sale_price;
      const category = categories.find((c) => c.name === qi.item.category)?.category_name || qi.item.category;

      if (!barcode) return [];

      return Array.from({ length: qi.copies }, () => ({
        title,
        subtitle,
        barcode,
        price,
        category,
      }));
    });

    if (!labelsToPrint.length) {
      alert('Could not build labels for the print queue. Check item codes and try again.');
      return;
    }

    await printBarcodeLabels(labelsToPrint, {
      codeType: resolvedCodeType,
      printPrice: resolvedPrintPrice,
      printCategory,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-primary-50 rounded-lg text-primary-600">
              <Printer className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Bulk Print Product Tags</h3>
              <p className="text-xs text-gray-500">Configure and print barcode/QR code tags in batches</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-200/60 rounded-xl transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Modal Body: Split Pane */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          
          {/* Left Pane: Search & Filter */}
          <div className="w-7/12 border-r border-gray-100 p-5 flex flex-col overflow-hidden">
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search products by name or code..."
                  value={modalSearch}
                  onChange={(e) => setModalSearch(e.target.value)}
                  className="input-field pl-9"
                />
              </div>
              <select
                value={modalCategory}
                onChange={(e) => setModalCategory(e.target.value)}
                className="input-field w-40"
              >
                <option value="All">All Categories</option>
                {productCategories.map((cat) => (
                  <option key={cat.name} value={cat.name}>
                    {cat.category_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Products List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {isLoadingItems ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-500 mb-2" />
                  <span className="text-sm">Loading items...</span>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">
                  No products found.
                </div>
              ) : (
                searchResults.map((item) => {
                  const isExpanded = !!expandedItems[item.name];
                  const itemVariantsList = loadedVariants[item.name] || [];
                  const isLoadVar = !!loadingVariants[item.name];

                  return (
                    <div key={item.name} className="border border-gray-100 rounded-xl p-3 bg-white hover:border-gray-200 transition-colors">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm truncate">{item.item_name}</h4>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                            <span>Code: {item.name}</span>
                            {item.barcode && <span>Barcode: {item.barcode}</span>}
                            <span className="font-medium text-primary-600">{formatMoney(item.sale_price)}</span>
                            <span className="bg-gray-100 px-1.5 py-0.5 rounded font-medium text-gray-700">Stock: {item.stock_quantity ?? 0}</span>
                          </div>
                        </div>

                        {item.has_variants ? (
                          <button
                            type="button"
                            onClick={() => handleToggleExpand(item)}
                            className="btn-secondary py-1.5 px-3 flex items-center gap-1.5 text-xs font-medium"
                          >
                            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            Options
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleAddToQueue(item)}
                            className="btn-primary py-1.5 px-3 flex items-center gap-1 text-xs font-semibold"
                            title={item.barcode ? 'Add to print queue' : 'Will use item code as barcode'}
                          >
                            <Plus className="w-3.5 h-3.5" /> Add
                          </button>
                        )}
                      </div>

                      {/* Variants section */}
                      {item.has_variants && isExpanded && (
                        <div className="mt-3 pt-3 border-t border-dashed border-gray-100 pl-4 space-y-2">
                          {isLoadVar ? (
                            <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading variants...
                            </div>
                          ) : itemVariantsList.length === 0 ? (
                            <div className="text-xs text-gray-400 py-1">No options defined.</div>
                          ) : (
                            itemVariantsList.map((variant) => (
                              <div key={variant.name} className="flex items-center justify-between gap-4 text-xs py-1">
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium text-gray-800">{variant.variant_name}</span>
                                  <div className="flex gap-x-3 mt-0.5 text-gray-500">
                                    {variant.barcode && <span>Barcode: {variant.barcode}</span>}
                                    <span className="text-primary-600 font-medium">{formatMoney(variant.price)}</span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleAddToQueue(item, variant)}
                                  className="p-1 hover:bg-primary-50 text-primary-600 rounded border border-gray-100"
                                  title={variant.barcode ? 'Add to print queue' : 'Will use item/variant code as barcode'}
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Pane: Print Queue */}
          <div className="w-5/12 p-5 flex flex-col overflow-hidden bg-gray-50/30">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900 text-sm">Print Queue</h4>
              <span className="text-xs font-semibold text-primary-700 bg-primary-50 py-1 px-2.5 rounded-full">
                Total Tags: {totalTags}
              </span>
            </div>

            {/* Queue List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {printQueue.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                  <Printer className="w-10 h-10 mb-2.5 text-gray-300 stroke-[1.5]" />
                  <p className="text-xs text-center">Your print queue is empty.<br />Add products from the list on the left.</p>
                </div>
              ) : (
                printQueue.map((qi) => (
                  <div key={qi.key} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm hover:border-gray-200 transition-colors flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-semibold text-gray-900 text-xs truncate">
                        {qi.item.item_name}
                      </h5>
                      {qi.variant && (
                        <div className="text-[10px] text-gray-500 font-medium mt-0.5">
                          Option: {qi.variant.variant_name}
                        </div>
                      )}
                      <div className="text-[10px] text-gray-400 mt-1 flex flex-wrap gap-x-2">
                        <span>Barcode: {qi.variant?.barcode || qi.item.barcode}</span>
                        <span>•</span>
                        <span>{formatMoney((qi.variant?.price ?? qi.item.sale_price))}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white">
                        <button
                          type="button"
                          onClick={() => handleUpdateCopies(qi.key, qi.copies - 1)}
                          className="p-1 hover:bg-gray-50 text-gray-500 transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <input
                          type="number"
                          value={qi.copies}
                          onChange={(e) => handleUpdateCopies(qi.key, parseInt(e.target.value, 10) || 1)}
                          className="w-10 text-center text-xs border-0 py-1 focus:ring-0 outline-none font-semibold text-gray-800"
                        />
                        <button
                          type="button"
                          onClick={() => handleUpdateCopies(qi.key, qi.copies + 1)}
                          className="p-1 hover:bg-gray-50 text-gray-500 transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveFromQueue(qi.key)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Prominent Configuration section */}
            <div className="mt-4 p-4 border border-gray-150 rounded-xl bg-white space-y-3 shadow-sm">
              <div className="flex items-center justify-between pb-1 border-b border-gray-100">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tag Layout Settings</span>
              </div>
              
              <div className="flex items-center justify-between gap-4 pt-1">
                <span className="text-xs font-semibold text-gray-600">Layout Format:</span>
                <div className="inline-flex border border-gray-200 rounded-lg p-0.5 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setCodeType('Barcode')}
                    className={`py-1 px-3 text-xs font-semibold rounded-md flex items-center gap-1 transition-all ${
                      resolvedCodeType === 'Barcode'
                        ? 'bg-white text-primary-700 shadow-sm border border-gray-200/50'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    <Barcode className="w-3.5 h-3.5" />
                    Barcode
                  </button>
                  <button
                    type="button"
                    onClick={() => setCodeType('QR Code')}
                    className={`py-1 px-3 text-xs font-semibold rounded-md flex items-center gap-1 transition-all ${
                      resolvedCodeType === 'QR Code'
                        ? 'bg-white text-primary-700 shadow-sm border border-gray-200/50'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    <QrCode className="w-3.5 h-3.5" />
                    QR Code
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 pt-1">
                <span className="text-xs font-semibold text-gray-600">Include Product Price:</span>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={resolvedPrintPrice}
                    onChange={(e) => setPrintPrice(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                  />
                  <span className="text-xs font-semibold text-gray-600">Print Price on Tag</span>
                </label>
              </div>

              <div className="flex items-center justify-between gap-4 pt-1">
                <span className="text-xs font-semibold text-gray-600">Include Category:</span>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={printCategory}
                    onChange={(e) => setPrintCategory(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                  />
                  <span className="text-xs font-semibold text-gray-600">Print Category on Tag</span>
                </label>
              </div>
            </div>

          </div>

        </div>

        {/* Modal Footer */}
        <div className="p-5 border-t border-gray-100 bg-gray-50/50 flex flex-wrap items-center justify-end gap-4">
          
          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClearQueue}
              disabled={printQueue.length === 0}
              className="btn-secondary px-4 text-xs font-semibold disabled:opacity-40 disabled:hover:bg-transparent"
            >
              Clear Queue
            </button>
            <div className="border-r border-gray-200 h-7" />
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-4 text-xs font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePrintBatch}
              disabled={printQueue.length === 0}
              className="btn-primary px-5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Printer className="w-3.5 h-3.5" />
              Print Batch Labels
            </button>
          </div>

        </div>

      </div>
    </div>
  );
}
